/** Where Product Type is stored. */
export const PRODUCT_TYPE_FIELD = {
    moduleId: 'classification',
    path: 'product_type',
    fieldId: 'cls_product_type',
};
const MOVING_HEAD_TYPES = [
    'moving-head-spot',
    'moving-head-profile',
    'moving-head-beam',
    'moving-head-wash',
    'moving-head-hybrid',
    'scanner',
];
const WASH_TYPES = [
    'par', 'wash-pod', 'panel', 'accent-pinspot', 'batten-strip', 'cyc', 'houselight',
];
const CONTROLLER_TYPES = ['controller-dmx', 'controller-wireless', 'data-distribution', 'rdm-tool'];
const CABLE_TYPES = ['cable-data', 'cable-power', 'cable-power-data'];
const RIGGING_TYPES = ['rigging-clamp', 'rigging-bar-bracket'];
const SHAPING_OPTIONS = ['barndoor', 'filter', 'gobo', 'honeycomb', 'intensifier', 'iris', 'lens'];
/**
 * Retired sub-type field (classification path) -> stored option -> the Product
 * Types it allows. A value naming a whole family narrows without deciding
 * ("moving-head"); a more specific field then picks one. A value that says
 * nothing about the type ("battery", "exterior": features) is left out.
 */
export const PRODUCT_TYPES_BY_SUBTYPE = {
    fixture: {
        ellipsoidal: ['ellipsoidal'], followspot: ['followspot'], fresnel: ['fresnel'], houselight: ['houselight'],
        'moving-head': MOVING_HEAD_TYPES, 'strobe-blinder': ['strobe-blinder'], 'wash-light': WASH_TYPES,
    },
    wash_light: {
        batten: ['batten-strip'], cove: ['batten-strip'], footlight: ['batten-strip'], graze: ['batten-strip'],
        cyclorama: ['cyc'], 'down-light': ['houselight', 'accent-pinspot'], panel: ['panel'], par: ['par'],
        pods: ['wash-pod'],
    },
    moving_head: {
        beam: ['moving-head-beam'], hybrid: ['moving-head-hybrid'], profile: ['moving-head-profile'],
        spot: ['moving-head-spot'], wash: ['moving-head-wash'],
        beamwash: ['moving-head-beam', 'moving-head-wash', 'moving-head-hybrid'], effect: MOVING_HEAD_TYPES,
    },
    led_display: { 'video-batten': ['video-pixel'], 'video-panel': ['video-panel'] },
    atmospheric: { fluid: ['atmospheric-fluid'], 'fog-machine': ['fog-machine'], 'haze-machine': ['haze-machine'] },
    control: { lighting: CONTROLLER_TYPES, production: CONTROLLER_TYPES },
    lighting_control: {
        'dmx-interface': ['controller-dmx'], 'wall-controller': ['controller-dmx'],
        'remote-controller': ['controller-wireless'], 'wireless-controller': ['controller-wireless'],
    },
    networking: { 'dmx-splitter': ['data-distribution'], 'network-node': ['data-distribution'] },
    accessory: {
        battery: ['accessory-battery', 'charging-case'], 'beam-shaping-effects': ['accessory-optical'],
        cable: CABLE_TYPES, cart: ['accessory-other'], sleeve: ['accessory-other'], 'rigging-mounting': RIGGING_TYPES,
        'road-case': ['road-case'], 'service-tool': ['rdm-tool'],
    },
    shaping: Object.fromEntries(SHAPING_OPTIONS.map((v) => [v, ['accessory-optical']])),
    rigging: {
        bracket: ['rigging-bar-bracket'], pendant: ['rigging-bar-bracket'], 'rig-bar': ['rigging-bar-bracket'],
        yoke: ['rigging-bar-bracket'], clamp: ['rigging-clamp'], 'safety-cable': ['rigging-clamp'], kit: RIGGING_TYPES,
        'trim-ring': ['rigging-bar-bracket', 'accessory-other'],
    },
    cable: {
        '3-pin': ['cable-data'], '5-pin': ['cable-data'], dmx: ['cable-data'], 'ip65-dmx': ['cable-data'],
        '4-pin': CABLE_TYPES,
        combo: ['cable-power-data'], powerkon: ['cable-power'], true1: ['cable-power'],
    },
};
/**
 * A classification value as its option slugs: "x", ["x"], or [{ slug: "x", name }]
 * (the taxonomy import's shape).
 */
export function getOptionSlugs(raw) {
    const list = Array.isArray(raw) ? raw : [raw];
    const slugOf = (v) => typeof v === 'string' ? v : v && typeof v === 'object' && 'slug' in v ? String(v.slug) : '';
    return list.map(slugOf).filter(Boolean);
}
export function deriveProductType(values) {
    let candidates = null;
    const paths = [];
    for (const [path, byValue] of Object.entries(PRODUCT_TYPES_BY_SUBTYPE)) {
        const slugs = getOptionSlugs(values[path]);
        if (!slugs.length)
            continue;
        paths.push(path);
        const allowed = new Set(slugs.flatMap((s) => byValue[s] ?? []));
        if (!allowed.size)
            continue; // a feature, not a type
        candidates = candidates ? candidates.filter((c) => allowed.has(c)) : [...allowed];
    }
    return paths.length ? { candidates: candidates ?? [], paths } : null;
}
/** Whether the rule lets a module show for this Product Type. No type set, or no rule: yes. */
export function specModuleMatches(def, productType) {
    return !def.showWhen || !productType || def.showWhen.in.includes(productType);
}
//# sourceMappingURL=spec-product-type.js.map